import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PlanPicker } from "@/pages/auth/RegisterPage";
import { ArrowRight, Award, CalendarDays, Mail, MapPin, Phone, ShieldCheck, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useSettings } from "@/hooks/useSettings";
import { PageIntro, Reveal, Shell } from "@/components/layout/Shell";

const useSubmit = path => {
  const [form, setForm] = useState({});
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const submit = async e => {
    e.preventDefault();
    setErr("");
    try {
      await api(path, { method: "POST", body: JSON.stringify(form) });
      setSent(true);
    } catch (e) { setErr(e.message); }
  };
  const bind = k => ({ value: form[k] || "", onChange: e => setForm({ ...form, [k]: e.target.value }) });
  return { form, sent, err, submit, bind };
};

export function ContactPage() {
  const { sent, err, submit, bind } = useSubmit("/contact");
  const settings = useSettings();
  return (
    <Shell>
      <main className="inner-page">
        <PageIntro crumb="DCEA / Contact" title={<>Say <em className="gold-text">hello.</em></>} description="Have a question or a suggestion? We would love to hear from you." />
        <div className="container contact-grid">
          <Reveal className="contact-info">
            <h3>Reach the DCEA office</h3>
            <div className="contact-row"><MapPin size={18} /><span data-testid="contact-address">{settings.address}</span></div>
            <div className="contact-row"><Mail size={18} /><span data-testid="contact-email">{settings.email}</span></div>
            <div className="contact-row"><Phone size={18} /><span data-testid="contact-phone">{settings.phone}</span></div>
            <div className="contact-row"><CalendarDays size={18} /><span data-testid="contact-hours">{settings.office_hours}</span></div>
            <div className="contact-map">
              <iframe title="DCEA office map" src={`https://www.google.com/maps?q=${encodeURIComponent(settings.map_query || settings.address || "Hubli-Dharwad, Karnataka")}&output=embed`} loading="lazy" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <form className="application-form" onSubmit={submit} data-testid="contact-form">
              {sent ? (
                <div className="success-message" data-testid="contact-success">
                  Thanks — your message has been received. The DCEA team will be in touch.
                </div>
              ) : (
                <>
                  <h2>Send us a message</h2>
                  {[["name", "Full name"], ["email", "Email address"], ["phone", "Phone number"], ["subject", "Subject"]].map(([k, l]) => (
                    <label key={k}>{l}
                      <input required={k !== "phone"} {...bind(k)} data-testid={`contact-${k}-input`} />
                    </label>
                  ))}
                  <label className="full">Message
                    <textarea required rows={5} {...bind("message")} data-testid="contact-message-input" />
                  </label>
                  {err && <div className="error-message" data-testid="contact-error">{err}</div>}
                  <button className="button button-gold" data-testid="contact-submit-button">
                    Send message <ArrowRight size={15} />
                  </button>
                </>
              )}
            </form>
          </Reveal>
        </div>
      </main>
    </Shell>
  );
}

const perks = [
  [Users, "Access to a 300+ strong professional network"],
  [Award, "Recognition, certificates and technical programs"],
  [ShieldCheck, "Representation and support for the profession"]
];

export function MembershipPage() {
  const [plans, setPlans] = useState([]);
  const [plan, setPlan] = useState("annual");
  useEffect(() => { api("/public/membership-plans").then(d => setPlans(d.plans || [])).catch(() => {}); }, []);
  return (
    <Shell>
      <main className="inner-page">
        <div className="container form-layout">
          <Reveal className="page-intro">
            <span className="eyebrow">Join the network</span>
            <h1>Build your next chapter <em className="gold-text">with DCEA.</em></h1>
            <p>Membership connects you to people, programs and opportunities that make the profession stronger. Choose a plan, create your account and pay securely — your Member ID is issued once the treasurer verifies the payment.</p>
            <div className="form-perks">
              {perks.map(([Icon, text], i) => (
                <div key={text} data-testid={`membership-perk-${i}`}><Icon size={18} />{text}</div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="application-form" data-testid="membership-plans">
              <h2>Membership plans</h2>
              <div className="full">{plans.length ? <PlanPicker plans={plans} value={plan} onChange={setPlan} /> : <div className="hint">Loading plans…</div>}</div>
              <Link to={`/register?plan=${plan}`} className="button button-gold" style={{ gridColumn: "1/-1", justifyContent: "center" }} data-testid="membership-join-button">
                Join with {plans.find(p => p.key === plan)?.label || "this plan"} <ArrowRight size={15} />
              </Link>
              <p className="auth-foot full" style={{ textAlign: "center" }}>Already a member? <Link to="/login" data-testid="membership-login-link">Sign in</Link></p>
            </div>
          </Reveal>
        </div>
      </main>
    </Shell>
  );
}
